import { countBy, identity } from 'es-toolkit';
import React from 'react';
import { connect, ResolveThunks } from 'react-redux';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { SimpleTreeView, TreeItem, TreeItemContent } from '@mui/x-tree-view';
import { Draft, produce } from 'immer';

import { ReadFile, SelectFile } from '@paragon/client-tools';
import { ShallowPdf } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { enqueueError } from '@paragon/notification-tools';
import { naturalSortByProperty } from '@paragon/utilities';

import { generateMergedPdf } from '../../actions/appActions';
import { RootState } from '../../store';

interface CheckedItemIds {
  readonly checked: readonly string[];
  readonly halfChecked: readonly string[];
}

const jobItemIdFromJobId = (jobId: number) => `job${jobId}`;
const fileItemIdFromFile = (file: ShallowPdf) => `${file.id}`;

const COVER_PAGE = 'coverpage.pdf';
const BACK_PAGE = 'backpage.pdf';
const FILE_NAMES_NOT_SELECTED_BY_DEFAULT = [COVER_PAGE, BACK_PAGE]; // Will be compared by lowercase
const NOT_DUPLICATES = [COVER_PAGE, BACK_PAGE];

const areAnyFilesSelectedForThisJob = (job: Job, checkedItemIds: CheckedItemIds) =>
  job.files.find((file) => checkedItemIds.checked.includes(fileItemIdFromFile(file)));

const getSortedFilesFromJob = ({ files }: Job) => {
  const coverPage = files.find((file) => file.name.toLowerCase() === COVER_PAGE);
  const backPage = files.find((file) => file.name.toLowerCase() === BACK_PAGE);
  const otherPages = files.filter((file) => ![COVER_PAGE, BACK_PAGE].includes(file.name.toLowerCase()));

  const sortedPages = naturalSortByProperty(otherPages, (file) => file.name);

  if (coverPage != null) {
    sortedPages.unshift(coverPage);
  }

  if (backPage != null) {
    sortedPages.push(backPage);
  }

  return sortedPages;
};

const areAllFilesSelectedForThisJob = (job: Job, checkedItemIds: CheckedItemIds) => {
  for (let i = 0; i < job.files.length; i++) {
    const currentFileItemId = fileItemIdFromFile(job.files[i]);
    if (!checkedItemIds.checked.includes(currentFileItemId)) return false;
  }

  return true;
};

const markChecked = (itemId: string, checkedItemIds: Draft<CheckedItemIds>) => {
  if (!checkedItemIds.checked.includes(itemId)) {
    checkedItemIds.halfChecked = checkedItemIds.halfChecked.filter((halfCheckedItemId) => halfCheckedItemId !== itemId);
    checkedItemIds.checked.push(itemId);
  }
};

const unmarkChecked = (itemId: string, checkedItemIds: Draft<CheckedItemIds>) => {
  checkedItemIds.checked = checkedItemIds.checked.filter((checkedItemId) => checkedItemId !== itemId);
};

const markHalfChecked = (itemId: string, checkedItemIds: Draft<CheckedItemIds>) => {
  if (!checkedItemIds.halfChecked.includes(itemId)) {
    checkedItemIds.checked = checkedItemIds.checked.filter((checkedItemId) => checkedItemId !== itemId);
    checkedItemIds.halfChecked.push(itemId);
  }
};

const unmarkHalfChecked = (itemId: string, checkedItemIds: Draft<CheckedItemIds>) => {
  checkedItemIds.halfChecked = checkedItemIds.halfChecked.filter((halfCheckedItemId) => halfCheckedItemId !== itemId);
};

const TreeItemContentGoldenrodBackground = styled(TreeItemContent)({
  backgroundColor: 'goldenrod',
  '&:hover': {
    backgroundColor: 'goldenrod',
  },
});

interface Job {
  readonly id: number;
  readonly name: string;
  readonly files: readonly ShallowPdf[];
}

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;
interface OwnProps {
  jobs: readonly Job[];
  onCloseClick: () => void;
}
type Props = StateProps & DispatchProps & OwnProps;

interface State {
  checkedItemIds: CheckedItemIds;
  generateMultiMergeCoverPdf: boolean;
  multiMergeJobName: string;
}

class DownloadGrid extends React.Component<Props, State> {
  state: State = {
    checkedItemIds: {
      checked: [],
      halfChecked: [],
    },
    generateMultiMergeCoverPdf: true,
    multiMergeJobName: this.props.jobs[0].name,
  };

  private fileSelect: SelectFile | null = null;

  getDuplicateFileNames() {
    const files = this.props.jobs.flatMap((job) => job.files);
    const fileCounts = countBy(
      files.map((file) => file.name),
      identity,
    );

    return Object.keys(fileCounts)
      .filter((fileName) => !NOT_DUPLICATES.includes(fileName.toLowerCase()) && fileCounts[fileName] > 1)
      .map((fileName) => fileName.toLowerCase());
  }

  handleJobCheckboxChange = (jobChecked: Job) => {
    this.setState((state: State) => {
      const nextCheckedItemIds = produce(state.checkedItemIds, (checkedItemIds) => {
        const jobCheckedItemId = jobItemIdFromJobId(jobChecked.id);
        if (checkedItemIds.checked.includes(jobCheckedItemId)) {
          // Was checked, make all unchecked
          const fileItemIdsForThisJob = jobChecked.files.map(fileItemIdFromFile);
          fileItemIdsForThisJob.forEach((fileItemIdForJob) => unmarkChecked(fileItemIdForJob, checkedItemIds));
          unmarkChecked(jobCheckedItemId, checkedItemIds);
        } else if (checkedItemIds.halfChecked.includes(jobCheckedItemId)) {
          // Was half checked, make all checked
          const fileItemIdsForThisJob = jobChecked.files.map(fileItemIdFromFile);
          fileItemIdsForThisJob.forEach((fileItemIdForJob) => markChecked(fileItemIdForJob, checkedItemIds));
          markChecked(jobCheckedItemId, checkedItemIds);
        } else {
          // Was not checked, make half checked
          const fileItemIdsForJobWithoutCoverAndBackPage = jobChecked.files.reduce<string[]>((jobFileItemIds, file) => {
            if (FILE_NAMES_NOT_SELECTED_BY_DEFAULT.includes(file.name.toLowerCase())) return jobFileItemIds;

            const fileItemId = fileItemIdFromFile(file);
            jobFileItemIds.push(fileItemId);
            return jobFileItemIds;
          }, []);
          fileItemIdsForJobWithoutCoverAndBackPage.forEach((fileItemIdForJob) =>
            markChecked(fileItemIdForJob, checkedItemIds),
          );
          markHalfChecked(jobCheckedItemId, checkedItemIds);
        }
      });

      return { checkedItemIds: nextCheckedItemIds };
    });
  };

  handleFileCheckboxChange = (job: Job, fileItemId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState((state: State) => {
      const nextCheckedItemIds = produce(state.checkedItemIds, (checkedItemIds) => {
        const jobItemId = jobItemIdFromJobId(job.id);
        if (event.target.checked) {
          markChecked(fileItemId, checkedItemIds);

          // Update job check state for this file if necessary
          if (areAllFilesSelectedForThisJob(job, checkedItemIds)) {
            markChecked(jobItemId, checkedItemIds);
          } else markHalfChecked(jobItemId, checkedItemIds);
        } else {
          unmarkChecked(fileItemId, checkedItemIds);

          // Update job check state for this file if necessary
          if (areAnyFilesSelectedForThisJob(job, checkedItemIds)) {
            markHalfChecked(jobItemId, checkedItemIds);
          } else {
            unmarkHalfChecked(jobItemId, checkedItemIds);
            unmarkChecked(jobItemId, checkedItemIds); // Handle jobs with only one PDF
          }
        }
      });

      return { checkedItemIds: nextCheckedItemIds };
    });
  };

  onToggleGenerateMultiMergeCoverPdf = () => {
    this.setState((state: State) => ({ generateMultiMergeCoverPdf: !state.generateMultiMergeCoverPdf }));
  };

  onMultiMergeJobNameChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    this.setState({ multiMergeJobName: event.target.value });
  };

  onGenerateMergedPdf = () => {
    if (this.state.checkedItemIds.checked.length === 0) {
      this.props.enqueueError('Please select at least one item');

      return;
    }

    const selectedFileIds = this.state.checkedItemIds.checked.reduce<number[]>((accumulator, itemId) => {
      if (itemId.substring(0, 3) !== 'job') {
        // Exclude job Ids ("job" has been prepended to IDs in the tree that are job IDs)
        accumulator.push(parseInt(itemId, 10));
      }
      return accumulator;
    }, []);
    this.props.generateMergedPdf({
      generateMultiMergeCoverPdf: this.state.generateMultiMergeCoverPdf,
      multiMergeJobName: this.state.multiMergeJobName,
      selectedFileIds,
    });
    this.props.onCloseClick();
  };

  render() {
    const { userOrganization } = this.props;

    const duplicateFiles = this.getDuplicateFileNames();

    const jobTreeItems = this.props.jobs.map((job) => (
      <TreeItem
        key={jobItemIdFromJobId(job.id)}
        itemId={jobItemIdFromJobId(job.id)}
        label={
          <FormControlLabel
            label={`${job.name} (Job ID: ${job.id})`}
            control={
              <Checkbox
                checked={this.state.checkedItemIds.checked.includes(jobItemIdFromJobId(job.id))}
                indeterminate={this.state.checkedItemIds.halfChecked.includes(jobItemIdFromJobId(job.id))}
                onChange={() => {
                  this.handleJobCheckboxChange(job);
                }}
              />
            }
          />
        }
      >
        {getSortedFilesFromJob(job).map((file) => (
          <TreeItem
            key={fileItemIdFromFile(file)}
            itemId={fileItemIdFromFile(file)}
            slots={{
              content: duplicateFiles.includes(file.name.toLowerCase())
                ? TreeItemContentGoldenrodBackground
                : TreeItemContent,
            }}
            label={
              <FormControlLabel
                label={file.name}
                control={
                  <Checkbox
                    checked={this.state.checkedItemIds.checked.includes(fileItemIdFromFile(file))}
                    onChange={(event) => {
                      this.handleFileCheckboxChange(job, fileItemIdFromFile(file), event);
                    }}
                  />
                }
              />
            }
          />
        ))}
      </TreeItem>
    ));

    const jobNameInput = (
      <TextField
        fullWidth
        variant="standard"
        label="Custom Job Name"
        value={this.state.multiMergeJobName}
        disabled={!this.state.generateMultiMergeCoverPdf}
        onChange={this.onMultiMergeJobNameChange}
        sx={{ marginBottom: 1 }}
      />
    );

    const onFileLoading = async (fileLoadingPromises: readonly Promise<ReadFile>[]) => {
      if (fileLoadingPromises.length !== 1) {
        this.props.enqueueError('Select exactly one CNT file.');
        return;
      }
      const fileLoadingPromise = fileLoadingPromises[0];
      const file = await fileLoadingPromise;
      const parsedCountFile = await designService.countUtilities.postParseQuantitiesFromCountFile(file);
      const componentNames = Object.keys(parsedCountFile).map((componentName) => componentName.toLowerCase());
      const rowItemIdsForComponentNames = this.props.jobs
        .flatMap(
          (job) =>
            job.files.filter((file) => componentNames.includes(file.name.toLowerCase().replace(/\.[^/.]+$/, ''))), // https://stackoverflow.com/a/4250408/4046605
        )
        .map(fileItemIdFromFile);
      const checkedItemIdsWithoutJobs: CheckedItemIds = {
        checked: rowItemIdsForComponentNames,
        halfChecked: [],
      };
      const jobsChecked = this.props.jobs.filter((job) =>
        areAllFilesSelectedForThisJob(job, checkedItemIdsWithoutJobs),
      );
      const jobsHalfChecked = this.props.jobs.filter(
        (job) => !jobsChecked.includes(job) && areAnyFilesSelectedForThisJob(job, checkedItemIdsWithoutJobs),
      );

      const checkedItemIds: CheckedItemIds = {
        checked: rowItemIdsForComponentNames.concat(jobsChecked.map((job) => jobItemIdFromJobId(job.id))),
        halfChecked: jobsHalfChecked.map((job) => jobItemIdFromJobId(job.id)),
      };

      this.setState({ checkedItemIds });
    };

    return (
      <Stack sx={{ padding: 2, backgroundColor: '#424242' }}>
        <Box sx={{ marginBottom: 1 }}>
          {jobNameInput}
          <FormControlLabel
            control={
              <Checkbox
                checked={this.state.generateMultiMergeCoverPdf}
                onChange={this.onToggleGenerateMultiMergeCoverPdf}
                color="primary"
              />
            }
            label="Generate custom cover page"
          />
        </Box>

        <Divider sx={{ marginBottom: 2 }} />

        <Box sx={{ marginBottom: 2 }}>
          <SimpleTreeView disableSelection expansionTrigger="iconContainer">
            {jobTreeItems}
          </SimpleTreeView>
        </Box>

        {(userOrganization === 'Clearspan Components Inc.' || userOrganization === 'Develop Organization A') && (
          <Box sx={{ marginBottom: 2 }}>
            <Button variant="outlined" onClick={() => this.fileSelect?.click()}>
              Upload Count File
              <SelectFile
                hide
                onChange={onFileLoading}
                accept=".cnt"
                ref={(item) => {
                  this.fileSelect = item;
                }}
              />
            </Button>
          </Box>
        )}

        <Box sx={{ marginBottom: 2 }}>
          <Typography sx={{ fontSize: '.75rem' }} align="center">
            Generate Merged PDF will give you a PDF that is not digitally signed and will therefore not meet the
            encryption required in most states. An encrypted PDF for each job can be obtained by clicking the name of
            the the job in the jobs table and then Download &gt; Merged Sealed PDF.
          </Typography>
        </Box>

        <Grid container direction="row" sx={{ justifyContent: 'flex-end', bgcolor: '#424242' }}>
          <Button sx={{ m: 1 }} variant="outlined" id="multiMergeDialogCloseButton" onClick={this.props.onCloseClick}>
            Close
          </Button>

          <Button
            sx={{ m: 1 }}
            variant="contained"
            id="multiMergeGenerateMergedPdfButton"
            onClick={this.onGenerateMergedPdf}
          >
            Generate Merged PDF
          </Button>
        </Grid>
      </Stack>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  userOrganization: state.logInData.loggedInUser!.organization,
});

const actionCreators = {
  enqueueError,
  generateMergedPdf,
};

export default connect(mapStateToProps, actionCreators)(DownloadGrid);
